import React from "react";
import { motion } from "framer-motion";
import {
  FaGraduationCap,
  FaUniversity,
  FaBookOpen,
  FaBrain,
  FaUsers,
  FaLaptopCode,
} from "react-icons/fa";

const activities = [
  {
    title: "Specialized Streams",
    desc: "Students choose Science, Commerce or Humanities according to their interests and future career goals.",
    icon: <FaBookOpen />,
  },
  {
    title: "Board Exam Preparation",
    desc: "Focused academic support prepares students confidently for board examinations and entrance tests.",
    icon: <FaGraduationCap />,
  },
  {
    title: "Career Guidance",
    desc: "Expert mentoring helps students identify career opportunities and higher education pathways.",
    icon: <FaUniversity />,
  },
  {
    title: "Leadership Skills",
    desc: "Seminars, debates and collaborative learning build confidence and leadership qualities.",
    icon: <FaUsers />,
  },
  {
    title: "Technology Integration",
    desc: "Students develop digital literacy through modern classrooms and technology-enabled learning.",
    icon: <FaLaptopCode />,
  },
  {
    title: "Life Skills",
    desc: "Responsibility, discipline and independent thinking prepare students for adulthood.",
    icon: <FaBrain />,
  },
];

const SeniorSecondary = () => {
  return (
    <section className="relative w-full py-32 overflow-hidden bg-gradient-to-br from-[#f8fbff] via-[#f5f7fc] to-[#eef4ff]">

      <div className="absolute top-[-120px] left-[-120px] w-[420px] h-[420px] bg-[#d4af37]/15 blur-3xl rounded-full"></div>

      <div className="absolute bottom-[-120px] right-[-120px] w-[420px] h-[420px] bg-[#07294d]/10 blur-3xl rounded-full"></div>

      <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#07294d_1px,transparent_1px),linear-gradient(to_bottom,#07294d_1px,transparent_1px)] bg-[size:70px_70px]"></div>

      <div className="max-w-[1550px] mx-auto px-6 relative z-10">

        {/* HERO */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-28"
        >

          <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white border border-[#d4af37]/40 shadow-[0_15px_40px_rgba(0,0,0,0.05)] mb-10">

            <span className="text-[#d4af37] text-xl">🎓</span>

            <p className="uppercase tracking-[6px] text-[#d4af37] text-sm font-black">
              Senior Secondary Education
            </p>

          </div>

          <h1 className="text-5xl md:text-7xl xl:text-8xl font-black text-[#07294d] leading-[1.08]">

            <span className="relative inline-block pb-8">

              Senior
              <br />
              Secondary School

              <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-[105%] h-6 bg-[#d4af37]/20 rounded-full"></span>

              <span className="absolute left-1/2 -translate-x-1/2 bottom-2 w-[70%] h-[4px] bg-white rounded-full"></span>

            </span>

          </h1>

          <p className="max-w-[1150px] mx-auto text-gray-600 text-xl leading-10 mt-14">

            Senior Secondary School is the final and most important
            stage of school education where students prepare for higher
            education, professional courses and successful careers
            through specialised academic learning and holistic development.

          </p>

        </motion.div>

        {/* MAIN CONTENT */}

        <div className="max-w-5xl mx-auto mb-32">

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="bg-white rounded-[50px] shadow-[0_30px_100px_rgba(0,0,0,0.08)] p-10 md:p-14 relative overflow-hidden">

              <div className="absolute top-0 left-0 w-full h-[8px] bg-gradient-to-r from-[#07294d] via-[#d4af37] to-[#07294d]"></div>

              <div className="absolute top-[-70px] right-[-70px] w-[220px] h-[220px] border-[24px] border-[#d4af37]/10 rounded-full"></div>

              <div className="w-24 h-24 rounded-[32px] bg-[#07294d] text-white flex items-center justify-center text-5xl shadow-2xl mb-10 relative z-10">

                <FaGraduationCap />

              </div>

              <h2 className="text-5xl font-black text-[#07294d] leading-tight mb-8 relative z-10">

                Preparing Students
                <br />
                For Bright Careers

              </h2>

              <div className="w-28 h-[6px] bg-[#d4af37] rounded-full mb-10"></div>

              <p className="text-gray-600 text-xl leading-10 mb-8">

                Senior Secondary School is the final and most crucial
                stage of school education, preparing students directly
                for higher education, professional courses and the job market.

              </p>

              <p className="text-gray-600 text-xl leading-10 mb-8">

                Students choose Science, Commerce or Humanities
                according to their interests, strengths and career goals,
                allowing them to focus on specialised subjects while
                preparing for board examinations and entrance tests.

              </p>

              <p className="text-gray-600 text-xl leading-10">

                Along with academic excellence, students develop
                independent thinking, self-discipline, leadership,
                communication skills and confidence through projects,
                seminars, extracurricular activities and expert guidance.

              </p>

            </div>

          </motion.div>

        </div>
                {/* ================= HIGHLIGHTS ================= */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >

          <h2 className="text-5xl md:text-6xl font-black text-[#07294d] leading-tight">

            <span className="relative inline-block pb-8">

              Senior Secondary Highlights

              <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-[105%] h-5 bg-[#d4af37]/20 rounded-full"></span>

            </span>

          </h2>

          <p className="max-w-4xl mx-auto text-gray-600 text-xl leading-9 mt-6">

            Our Senior Secondary programme empowers students with specialised
            knowledge, confidence, leadership and career-focused learning for
            higher education and lifelong success.

          </p>

        </motion.div>

        {/* ================= GRID ================= */}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-10">

          {activities.map((item, index) => (

            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -12 }}
              className="group relative overflow-hidden rounded-[40px] bg-white shadow-[0_25px_80px_rgba(0,0,0,0.08)]"
            >

              <div className="absolute top-0 left-0 w-full h-[7px] bg-gradient-to-r from-[#07294d] via-[#d4af37] to-[#07294d]"></div>

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-br from-[#d4af37]/10 to-[#07294d]/10"></div>

              <h1 className="absolute top-4 right-6 text-[90px] font-black text-[#07294d]/5">

                0{index + 1}

              </h1>

              <div className="relative z-10 p-10">

                <div className="w-20 h-20 rounded-[28px] bg-[#07294d] text-white flex items-center justify-center text-4xl shadow-2xl mb-8 group-hover:bg-[#d4af37] group-hover:text-[#07294d] transition-all duration-500">

                  {item.icon}

                </div>

                <h3 className="text-3xl font-black text-[#07294d] mb-5 leading-tight">

                  {item.title}

                </h3>

                <div className="w-20 h-[5px] bg-[#d4af37] rounded-full mb-7"></div>

                <p className="text-gray-600 text-lg leading-9">

                  {item.desc}

                </p>

              </div>

            </motion.div>

          ))}

        </div>
              </div>

    </section>
  );
};

export default SeniorSecondary;